/* eslint-disable no-console */
import React from 'react';

import RefreshIcon from '@mui/icons-material/Refresh';
import { Alert, Box, Button, CircularProgress } from '@mui/material';
import LibraryComponent from 'components/Library';
import LibraryData from 'components/Library/types';
import { ThreeJSImage } from 'components/threejs-image';
import { ToolPanel } from 'components/ToolPanel';
import { useSignalRState } from 'contexts/SignalRContext';
import { usePreviewLogic } from 'pages/Preview/preview.logic';
import { usePreviewStyles } from 'pages/Preview/preview.styles';
import { useLocation } from 'react-router-dom';

import { MainWrapper } from '../../styles/styles';

export default function Preview() {
   const location = useLocation();
   const isSharedPreviewRoute = location.pathname.startsWith('/preview/shared');

   const { contentWrapper, mainContentWrapper, tableWrapper } = usePreviewStyles();
   const { decodedThreeJSData, isLibraryVisible, expirationMessage, showToolPanel, handlers } =
      usePreviewLogic();
   const { error } = useSignalRState();

   const hasDecodedImage = Boolean(decodedThreeJSData.printColor && decodedThreeJSData.printBW);
   const shouldRenderThreeJSImage = hasDecodedImage;

   const handleBackToUpload = () => {
      // Clear selected layer from sessionStorage when going back to upload
      try {
         sessionStorage.removeItem('previewLayer');
      } catch (e) {
         // ignore
      }
      window.location.href = '/upload';
   };

   const showMessage = error || expirationMessage;

   return (
      <>
         <div
            className={
               isSharedPreviewRoute
                  ? 'share-bright-spark min-h-screen w-full bg-background text-foreground'
                  : undefined
            }
         >
            <MainWrapper>
               {/* Show expiration alert if message exists */}
               {expirationMessage && (
                  <Alert
                     severity='warning'
                     sx={{
                        position: 'fixed',
                        top: isSharedPreviewRoute ? 16 : 80,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        zIndex: 1000,
                        minWidth: 400,
                        maxWidth: '80%',
                     }}
                  >
                     {expirationMessage}
                  </Alert>
               )}

               <Box
                  component='section'
                  sx={{
                     ...mainContentWrapper,
                     flex: 1,
                     minWidth: 0,
                     flexDirection: 'row',
                     display: 'flex',
                     overflow: 'hidden',
                  }}
               >
               {shouldRenderThreeJSImage && showToolPanel ? <ToolPanel /> : null}
               <Box
                  component='div'
                  sx={{
                     ...contentWrapper,
                     flex: 1,
                     minWidth: 0,
                     position: 'relative',
                     overflow: 'hidden',
                  }}
               >
                  {shouldRenderThreeJSImage ? (
                     <ThreeJSImage
                        printColor={decodedThreeJSData.printColor}
                        printBW={decodedThreeJSData.printBW}
                        noiseTex={process.env.PUBLIC_URL + '/env/noise2n.webp'}
                        matCap={process.env.PUBLIC_URL + '/matcaps/006.exr'}
                        envTex={process.env.PUBLIC_URL + '/env/environment.hdr'}
                     />
                  ) : showMessage ? (
                     // Show message and back button when error/expiration occurs
                     <Box
                        sx={{
                           display: 'flex',
                           flexDirection: 'column',
                           alignItems: 'center',
                           justifyContent: 'center',
                           height: '100%',
                           gap: 2,
                        }}
                     >
                        <Box
                           sx={{
                              color: 'text.secondary',
                              textAlign: 'center',
                              mb: 2,
                           }}
                        >
                           {error ? 'Error occurred' : 'Preview not available'}
                        </Box>
                        {/* Back to Upload Button */}
                        <Button
                           variant='contained'
                           startIcon={<RefreshIcon />}
                           onClick={handleBackToUpload}
                           sx={{
                              backgroundColor: '#e0c78d',
                              color: '#000000',
                              '&:hover': { backgroundColor: '#d7b86d' },
                           }}
                        >
                           Back to Upload
                        </Button>
                     </Box>
                  ) : (
                     <Box
                        sx={{
                           display: 'flex',
                           alignItems: 'center',
                           justifyContent: 'center',
                           height: '100%',
                           width: '100%',
                        }}
                     >
                        <CircularProgress
                           size={48}
                           sx={{ color: '#c3a05f' }}
                        />
                     </Box>
                  )}
               </Box>
               {isLibraryVisible ? (
                  <Box component='div' sx={tableWrapper}>
                     <LibraryComponent
                        LibraryData={LibraryData}
                        onCellClick={handlers.handleLibraryCellClick}
                     />
                  </Box>
               ) : null}
               </Box>
            </MainWrapper>
         </div>
      </>
   );
}
